import { requireElement, setStatus } from "../core/utils/dom";
import { readFileAsDataUrl } from "../core/utils/file";
import type { GeoJsonDocument, GeoJsonGeometry, JsonValue } from "../core/types/geojson";
import { coerceStandardProperty, getPropertySchema, standardPropertyKeys, type EditableGeometryType, type PropertyFieldDef } from "../core/constants/featurePropertySchema";
import type { Page } from "./Page";
import { TerraDrawService, type DrawMode } from "../features/drawing/TerraDrawService";
import { GeoJsonBounds } from "../features/geojson/GeoJsonBounds";
import { GeoJsonExporter } from "../features/geojson/GeoJsonExporter";
import { GeoJsonLoader } from "../features/geojson/GeoJsonLoader";
import { GeoJsonParser } from "../features/geojson/GeoJsonParser";
import { ImageOverlayService } from "../features/map/ImageOverlayService";
import { MapService } from "../features/map/MapService";

const RESERVED_CUSTOM_PROPERTY_KEYS = new Set([
  "mode",
  "currentlyDrawing",
  "edited",
  "closingPoint",
  "closingPointId",
  "snappingPoint",
  "coordinatePoint",
  "coordinatePointId",
  "coordinatePointIds",
  "provisionalCoordinateCount",
  "committedCoordinateCount",
  "marker",
  "selectionPoint",
  "selectionPointFeatureId",
  "midPoint",
  "selected"
]);

const PROPERTY_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_:-]*$/;

export class DrawMapPage implements Page {
  private mapService = new MapService();
  private drawingService: TerraDrawService | null = null;
  private imageOverlay: ImageOverlayService | null = null;

  render(): HTMLElement {
    const page = document.createElement("main");
    page.className = "page page--drawer";
    page.innerHTML = `
      <section class="hero card">
        <div>
          <p class="eyebrow">Terra Draw editing workspace</p>
          <h1>Draw and export GeoJSON</h1>
          <p>Create points, open lines, closed boundary regions, polygons, rectangles, and freehand areas. Import existing GeoJSON, edit feature properties, and export a clean FeatureCollection.</p>
        </div>
      </section>

      <section class="toolbar card">
        <div class="field">
          <label for="draw-map-file">Import Map GeoJSON</label>
          <input id="draw-map-file" type="file" accept=".geojson,.json,application/geo+json,application/json" />
        </div>
        <div class="field">
          <label for="background-image">Import Image Overlay</label>
          <input id="background-image" type="file" accept="image/*" />
        </div>
        <button data-mode="render" type="button">Pan</button>
        <button data-mode="select" type="button">Select/Edit</button>
        <button data-mode="point" type="button">Point</button>
        <button data-mode="linestring" type="button">Open Line</button>
        <button data-mode="polyline" type="button">Polyline Boundary / Region</button>
        <button data-mode="polygon" type="button">Polygon</button>
        <button data-mode="rectangle" type="button">Rectangle</button>
        <button data-mode="freehand" type="button">Freehand Area</button>
        <button id="undo-draw" type="button">Undo</button>
        <button id="redo-draw" type="button">Redo</button>
        <button id="delete-selected" type="button">Delete Selected</button>
        <button id="clear-map" type="button">Clear</button>
        <button id="fit-features" type="button">Fit to Features</button>
        <button id="copy-geojson" type="button">Copy GeoJSON</button>
        <button id="create-geojson" type="button">Create GeoJSON</button>
        <button id="export-geojson" type="button">Export GeoJSON</button>
      </section>

      <section class="drawer-layout">
        <aside class="card panel">
          <h2>Image overlay</h2>
          <p class="muted">The uploaded image is added across the current map bounds. Move, scale, rotate, or change opacity to align it with the map.</p>
          <label>Overlay opacity <input id="image-opacity" type="range" min="0" max="1" step="0.05" value="0.65" /></label>
          <div class="button-grid">
            <button id="image-grow" type="button">Image +</button>
            <button id="image-shrink" type="button">Image -</button>
            <button data-move="up" type="button">Up</button>
            <button data-move="down" type="button">Down</button>
            <button data-move="left" type="button">Left</button>
            <button data-move="right" type="button">Right</button>
            <button id="image-rotate-left" type="button">Rotate left</button>
            <button id="image-rotate-right" type="button">Rotate right</button>
            <button id="image-remove" type="button">Remove Image</button>
          </div>

          <h2>Usage notes</h2>
          <ol class="compact-list">
            <li>Use Point, Open Line, Polyline Boundary / Region, Polygon, Rectangle, or Freehand Area to create geometry.</li>
            <li>Use Freehand Area when tracing coastlines, scanned maps, irregular boundaries, or hand-drawn research areas.</li>
            <li>Use Polyline Boundary / Region to click boundary points. Hover over the first point until the cursor changes to a hand/pointer, then click it to close the area as a filled polygon.</li>
            <li>Use Open Line when the result must remain a LineString. Press <kbd>Enter</kbd> to finish open lines or polygons and <kbd>Escape</kbd> to cancel.</li>
            <li>Use Select/Edit to drag features and edit vertices. For lines and polygons, drag visible vertices; drag midpoint handles to add new points when available.</li>
            <li>Standard property names are fixed to prevent typos; custom properties are checked for duplicates and reserved drawing-engine names before saving.</li>
            <li>Imported MultiPolygon, MultiLineString, and MultiPoint features are split into editable simple features.</li>
          </ol>
        </aside>

        <div class="map-shell map-shell--drawer card">
          <div id="draw-map" class="map"></div>
        </div>
      </section>

      <section class="card">
        <h2>Selected feature</h2>
        <div id="feature-editor" class="feature-editor muted">Choose Select/Edit, then click a feature to edit its properties and shape points.</div>
      </section>

      <section class="card">
        <h2>GeoJSON output</h2>
        <div id="drawer-status" class="status status--info">Initializing map...</div>
        <div id="drawer-summary" class="help-panel small-text">No features yet.</div>
        <textarea id="geojson-output" class="code-area code-area--large" spellcheck="false"></textarea>
      </section>
    `;

    queueMicrotask(() => void this.initialize(page));
    return page;
  }

  destroy(): void {
    this.drawingService?.stop();
    this.mapService.destroy();
  }

  private async initialize(page: HTMLElement): Promise<void> {
    const status = requireElement<HTMLDivElement>("#drawer-status", page);
    const output = requireElement<HTMLTextAreaElement>("#geojson-output", page);

    try {
      const map = this.mapService.initialize(requireElement("#draw-map", page), { zoom: 4 });
      await this.mapService.waitUntilReady();
      this.imageOverlay = new ImageOverlayService(map);
      this.drawingService = new TerraDrawService(map, {
        onChange: (collection) => {
          this.updateGeoJsonOutput(page, collection);
        },
        onSelect: () => this.renderFeatureEditor(page),
        onFinish: (collection) => {
          this.updateGeoJsonOutput(page, collection);
        },
        onMessage: (message, tone = "info") => {
          setStatus(status, message, tone);
        }
      });
      this.drawingService.start();
      this.bindUi(page);
      this.updateGeoJsonOutput(page, this.drawingService.getFeatureCollection());
      setStatus(status, "Map drawer is ready. Select a drawing mode from the toolbar.", "success");
    } catch (error) {
      setStatus(status, error instanceof Error ? error.message : "Failed to initialize map drawer.", "error", 0);
    }
  }

  private bindUi(page: HTMLElement): void {
    const status = requireElement<HTMLDivElement>("#drawer-status", page);
    const output = requireElement<HTMLTextAreaElement>("#geojson-output", page);

    page.querySelectorAll<HTMLButtonElement>("[data-mode]").forEach((button) => {
      button.addEventListener("click", () => {
        const mode = button.dataset.mode as DrawMode;
        this.drawingService?.setMode(mode);
        page.querySelectorAll<HTMLButtonElement>("[data-mode]").forEach((item) => item.classList.toggle("active", item === button));
        setStatus(status, this.describeMode(mode), "info");
      });
    });

    requireElement<HTMLInputElement>("#draw-map-file", page).addEventListener("change", async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file || !this.drawingService) return;
      try {
        const document = await GeoJsonLoader.fromFile(file);
        const result = this.drawingService.importGeoJson(document);
        this.updateGeoJsonOutput(page, this.drawingService.getFeatureCollection());
        const rejectionDetails = result.reasons.length ? ` Reasons: ${result.reasons.join("; ")}` : "";
        const multiWarning = this.getMultiGeometryWarning(document);
        const viewportNotice = GeoJsonBounds.viewportNotice(document);
        setStatus(
          status,
          `Imported ${result.imported} drawable feature(s) from ${file.name}${result.rejected ? `; ${result.rejected} rejected.` : "."}${rejectionDetails}${multiWarning}${viewportNotice}`,
          result.rejected ? "info" : "success",
          0
        );
      } catch (error) {
        setStatus(status, error instanceof Error ? error.message : "Could not import GeoJSON.", "error", 0);
      }
    });

    requireElement<HTMLInputElement>("#background-image", page).addEventListener("change", async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file || !this.imageOverlay) return;
      try {
        this.imageOverlay.load(await readFileAsDataUrl(file));
        setStatus(status, `Loaded image overlay ${file.name}.`, "success");
      } catch (error) {
        setStatus(status, error instanceof Error ? error.message : "Could not load image overlay.", "error", 0);
      }
    });

    requireElement<HTMLButtonElement>("#undo-draw", page).addEventListener("click", () => {
      const changed = this.drawingService?.undo() ?? false;
      this.updateGeoJsonOutput(page);
      setStatus(status, changed ? "Undo completed." : "There is no drawing action to undo.", changed ? "success" : "info");
    });
    requireElement<HTMLButtonElement>("#redo-draw", page).addEventListener("click", () => {
      const changed = this.drawingService?.redo() ?? false;
      this.updateGeoJsonOutput(page);
      setStatus(status, changed ? "Redo completed." : "There is no drawing action to redo.", changed ? "success" : "info");
    });
    requireElement<HTMLButtonElement>("#delete-selected", page).addEventListener("click", () => {
      const deleted = this.drawingService?.deleteSelected() ?? false;
      this.updateGeoJsonOutput(page);
      setStatus(status, deleted ? "Selected feature deleted." : "Select a feature before deleting.", deleted ? "success" : "info");
    });
    requireElement<HTMLButtonElement>("#clear-map", page).addEventListener("click", () => {
      this.drawingService?.clear();
      this.updateGeoJsonOutput(page);
      setStatus(status, "Map features cleared.", "info");
    });

    requireElement<HTMLButtonElement>("#fit-features", page).addEventListener("click", () => {
      const fitted = this.drawingService?.fitToFeatures() ?? false;
      setStatus(status, fitted ? "Map view fitted to the current features." : "There are no features to fit yet.", fitted ? "success" : "info");
    });

    requireElement<HTMLButtonElement>("#copy-geojson", page).addEventListener("click", async () => {
      try {
        this.updateGeoJsonOutput(page);
        await navigator.clipboard.writeText(output.value);
        setStatus(status, "GeoJSON copied to clipboard.", "success");
      } catch {
        output.focus();
        output.select();
        setStatus(status, "Clipboard access was not available. The GeoJSON text is selected so you can copy it manually.", "info");
      }
    });

    requireElement<HTMLButtonElement>("#create-geojson", page).addEventListener("click", () => {
      if (!this.drawingService) return;
      this.updateGeoJsonOutput(page, this.drawingService.getFeatureCollection());
      setStatus(status, "GeoJSON generated from current map features.", "success");
    });

    requireElement<HTMLButtonElement>("#export-geojson", page).addEventListener("click", () => {
      if (!this.drawingService) return;
      const document = this.drawingService.getFeatureCollection();
      this.updateGeoJsonOutput(page, document);
      GeoJsonExporter.download(document);
      setStatus(status, "GeoJSON file downloaded.", "success");
    });

    requireElement<HTMLInputElement>("#image-opacity", page).addEventListener("input", (event) => {
      this.imageOverlay?.setOpacity(Number((event.target as HTMLInputElement).value));
    });
    requireElement<HTMLButtonElement>("#image-grow", page).addEventListener("click", () => this.imageOverlay?.scale(1.12));
    requireElement<HTMLButtonElement>("#image-shrink", page).addEventListener("click", () => this.imageOverlay?.scale(0.88));
    requireElement<HTMLButtonElement>("#image-rotate-left", page).addEventListener("click", () => this.imageOverlay?.rotate(-5));
    requireElement<HTMLButtonElement>("#image-rotate-right", page).addEventListener("click", () => this.imageOverlay?.rotate(5));
    requireElement<HTMLButtonElement>("#image-remove", page).addEventListener("click", () => this.imageOverlay?.remove());
    page.querySelectorAll<HTMLButtonElement>("[data-move]").forEach((button) => {
      button.addEventListener("click", () => this.imageOverlay?.move(button.dataset.move as "up" | "down" | "left" | "right"));
    });
  }

  private updateGeoJsonOutput(page: HTMLElement, collection = this.drawingService?.getFeatureCollection() ?? { type: "FeatureCollection" as const, features: [] }): void {
    requireElement<HTMLTextAreaElement>("#geojson-output", page).value = GeoJsonParser.stringify(collection);
    const counts = collection.features.reduce<Record<string, number>>((acc, feature) => {
      const type = feature.geometry?.type ?? "Null geometry";
      acc[type] = (acc[type] ?? 0) + 1;
      return acc;
    }, {});
    const countText = Object.entries(counts).map(([type, count]) => `${type}: ${count}`).join(" · ") || "No features yet";
    requireElement<HTMLDivElement>("#drawer-summary", page).textContent = `Features: ${collection.features.length}. ${countText}. Use Create GeoJSON before copying/exporting if you want to refresh manually.`;
  }

  private describeMode(mode: DrawMode): string {
    const descriptions: Record<DrawMode, string> = {
      render: "Pan mode: move around the map without editing features.",
      select: "Select/Edit mode: click a feature to move it, edit vertices, or update properties.",
      point: "Point mode: click once to add a marker.",
      linestring: "Open Line mode: click points along a path, then press Enter to finish it as a LineString.",
      polyline: "Polyline Boundary / Region mode: click boundary points. When the cursor becomes a hand over the first point, click it to close the shape as a transparent polygon. Press Enter to keep it as an open line.",
      polygon: "Polygon mode: click boundary points, then press Enter to finish or Escape to cancel.",
      rectangle: "Rectangle mode: drag to create a rectangular area.",
      freehand: "Freehand Area mode: drag to trace an irregular area. After finishing, use Select/Edit to move the area and adjust its vertices."
    };
    return descriptions[mode];
  }

  private renderFeatureEditor(page: HTMLElement): void {
    const editor = requireElement<HTMLDivElement>("#feature-editor", page);
    const feature = this.drawingService?.getSelectedApplicationFeature();
    if (!feature) {
      editor.className = "feature-editor muted";
      editor.textContent = "Choose Select/Edit, then click a feature to edit its properties and shape points.";
      return;
    }

    const geometryType = feature.geometryType as EditableGeometryType;
    const schema = getPropertySchema(geometryType).filter((field) => !(geometryType === "Point" && ["label", "icon"].includes(field.key)));
    const standardKeys = standardPropertyKeys(geometryType);

    editor.className = "feature-editor";
    editor.innerHTML = `
      <p><strong>Geometry:</strong> ${geometryType}</p>
      <label>Name <input id="feature-name" type="text" value="${this.escapeAttribute(String(feature.properties.name ?? ""))}" /></label>

      ${geometryType === "Point" ? this.renderMarkerFields(feature.properties) : ""}

      <div class="section-title-row">
        <h3>Standard properties</h3>
      </div>
      <p class="muted">Property names are fixed to match the supported GeoJSON styling fields. Empty optional fields are not exported.</p>
      <table class="builder-table property-table" id="standard-property-table">
        <thead><tr><th>Property</th><th>Value</th><th>Help</th></tr></thead>
        <tbody></tbody>
      </table>

      <div class="section-title-row">
        <h3>Custom properties</h3>
        <button id="add-feature-property" type="button">Add custom property</button>
      </div>
      <p class="muted">Use custom properties for research metadata such as <code>source</code>, <code>period</code>, <code>region_code</code>, or <code>notes</code>. Standard styling fields are edited above. Property names must start with a letter or underscore and cannot use reserved drawing-engine names such as <code>mode</code> or <code>selected</code>.</p>
      <table class="builder-table property-table" id="custom-property-table">
        <thead><tr><th></th><th>Property</th><th>Value</th></tr></thead>
        <tbody></tbody>
      </table>

      <details open>
        <summary>Advanced JSON preview</summary>
        <p class="muted small-text">Live read-only preview of the properties that will be saved/exported.</p>
        <textarea id="feature-properties-json" class="code-area" spellcheck="false" readonly></textarea>
      </details>
      <button id="save-feature-properties" type="button">Save Properties</button>
    `;

    const standardBody = requireElement<HTMLTableSectionElement>("#standard-property-table tbody", editor);
    for (const field of schema) this.addStandardPropertyRow(standardBody, field, feature.properties);

    const customBody = requireElement<HTMLTableSectionElement>("#custom-property-table tbody", editor);
    for (const [key, value] of Object.entries(feature.properties)) {
      if (!standardKeys.has(key)) this.addFeaturePropertyRow(customBody, key, value);
    }

    const updatePreview = () => {
      try {
        requireElement<HTMLTextAreaElement>("#feature-properties-json", editor).value = JSON.stringify(this.collectFeatureEditorProperties(editor, standardBody, customBody, standardKeys, geometryType), null, 2);
      } catch (error) {
        requireElement<HTMLTextAreaElement>("#feature-properties-json", editor).value = error instanceof Error ? error.message : "Invalid property value.";
      }
    };

    editor.addEventListener("input", updatePreview);
    requireElement<HTMLButtonElement>("#add-feature-property", editor).addEventListener("click", () => {
      this.addFeaturePropertyRow(customBody);
      updatePreview();
    });
    customBody.addEventListener("click", (event) => {
      if ((event.target as HTMLElement).matches("button")) queueMicrotask(updatePreview);
    });

    requireElement<HTMLButtonElement>("#save-feature-properties", editor).addEventListener("click", () => {
      const status = requireElement<HTMLDivElement>("#drawer-status", page);
      try {
        const properties = this.collectFeatureEditorProperties(editor, standardBody, customBody, standardKeys, geometryType);
        this.drawingService?.updateSelectedProperties(properties);
        updatePreview();
        setStatus(status, "Feature properties saved.", "success");
      } catch (error) {
        setStatus(status, error instanceof Error ? error.message : "Invalid feature properties.", "error", 0);
      }
    });

    updatePreview();
  }

  private renderMarkerFields(properties: Record<string, JsonValue>): string {
    const label = this.objectValue(properties.label);
    const icon = properties.icon;
    const iconUrl = typeof icon === "string" ? icon : this.objectValue(icon)?.url ?? this.objectValue(icon)?.path ?? "";
    return `
      <fieldset class="marker-fieldset">
        <legend>Marker label and icon</legend>
        <p class="muted small-text">Use these fields instead of writing JSON. Leave the icon URL empty to use the default marker.</p>
        <div class="form-grid">
          <label>Label text <input id="marker-label-text" type="text" value="${this.escapeAttribute(String(label?.text ?? ""))}" placeholder="Cairo" /></label>
          <label>Label color <input id="marker-label-color" type="color" value="${this.normalizeColor(String(label?.color ?? "#111827"))}" /></label>
          <label>Font weight
            <select id="marker-label-weight">
              <option value="">Default</option>
              <option value="regular" ${label?.fontWeight === "regular" ? "selected" : ""}>Regular</option>
              <option value="bold" ${label?.fontWeight === "bold" ? "selected" : ""}>Bold</option>
            </select>
          </label>
          <label>Font size <input id="marker-label-size" type="text" value="${this.escapeAttribute(String(label?.fontSize ?? ""))}" placeholder="12px" /></label>
          <label>Icon URL <input id="marker-icon-url" type="text" value="${this.escapeAttribute(String(iconUrl ?? ""))}" placeholder="Leave empty for default marker" /></label>
        </div>
      </fieldset>
    `;
  }

  private collectFeatureEditorProperties(editor: HTMLElement, standardBody: HTMLTableSectionElement, customBody: HTMLTableSectionElement, standardKeys: Set<string>, geometryType: EditableGeometryType): Record<string, JsonValue> {
    const properties = this.readStandardPropertyRows(standardBody);
    Object.assign(properties, this.readMarkerPropertyFields(editor, geometryType));
    Object.assign(properties, this.readFeaturePropertyRows(customBody, standardKeys));
    properties.name = requireElement<HTMLInputElement>("#feature-name", editor).value;
    return properties;
  }

  private readMarkerPropertyFields(editor: HTMLElement, geometryType: EditableGeometryType): Record<string, JsonValue> {
    if (geometryType !== "Point") return {};
    const result: Record<string, JsonValue> = {};
    const labelText = requireElement<HTMLInputElement>("#marker-label-text", editor).value.trim();
    const labelColor = requireElement<HTMLInputElement>("#marker-label-color", editor).value.trim();
    const fontWeight = requireElement<HTMLSelectElement>("#marker-label-weight", editor).value.trim();
    const fontSize = requireElement<HTMLInputElement>("#marker-label-size", editor).value.trim();
    const iconUrl = requireElement<HTMLInputElement>("#marker-icon-url", editor).value.trim();

    if (fontSize && !/^\d+(px|em|rem|%)$/.test(fontSize)) {
      throw new Error("Marker font size must use a CSS unit, for example 12px, 1rem, or 80%.");
    }

    const label: Record<string, JsonValue> = {};
    if (labelText) label.text = labelText;
    if (labelColor) label.color = labelColor;
    if (fontWeight) label.fontWeight = fontWeight;
    if (fontSize) label.fontSize = fontSize;
    if (Object.keys(label).length) result.label = label;
    if (iconUrl) result.icon = iconUrl;
    return result;
  }

  private objectValue(value: JsonValue | undefined): Record<string, JsonValue> | null {
    return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, JsonValue>) : null;
  }

  private addStandardPropertyRow(tbody: HTMLTableSectionElement, field: PropertyFieldDef, properties: Record<string, JsonValue>): void {
    const row = document.createElement("tr");
    row.dataset.standardPropertyRow = "true";
    row.dataset.standardPropertyKey = field.key;

    const keyCell = row.insertCell();
    const valueCell = row.insertCell();
    const helpCell = row.insertCell();

    const currentValue = this.resolvePropertyValue(field, properties);
    keyCell.innerHTML = `<code>${this.escapeHtml(field.key)}</code>`;
    if (field.aliases?.length) keyCell.innerHTML += `<div class="muted small-text">aliases: ${field.aliases.map((alias) => `<code>${this.escapeHtml(alias)}</code>`).join(", ")}</div>`;

    const control = this.createStandardControl(field, currentValue);
    valueCell.append(control);
    helpCell.textContent = field.help;
    tbody.append(row);
  }

  private createStandardControl(field: PropertyFieldDef, value: JsonValue | undefined): HTMLElement {
    if (field.control === "json") {
      const textarea = document.createElement("textarea");
      textarea.dataset.standardPropertyValue = "true";
      textarea.className = "property-json-input";
      textarea.placeholder = field.placeholder ?? "JSON value";
      textarea.value = value === undefined ? "" : this.propertyValueToText(value);
      return textarea;
    }

    const input = document.createElement("input");
    input.dataset.standardPropertyValue = "true";
    input.type = field.control === "color" ? "color" : field.control === "number" ? "number" : "text";
    input.value = this.standardValueToText(field, value);
    if (field.min !== undefined) input.min = String(field.min);
    if (field.max !== undefined) input.max = String(field.max);
    if (field.step !== undefined) input.step = String(field.step);
    if (field.placeholder) input.placeholder = field.placeholder;
    return input;
  }

  private readStandardPropertyRows(tbody: HTMLTableSectionElement): Record<string, JsonValue> {
    const properties: Record<string, JsonValue> = {};
    for (const row of Array.from(tbody.querySelectorAll<HTMLTableRowElement>("[data-standard-property-row]"))) {
      const key = row.dataset.standardPropertyKey;
      if (!key) continue;
      const field = this.findFieldForRow(row, key);
      const control = requireElement<HTMLInputElement | HTMLTextAreaElement>("[data-standard-property-value]", row);
      const coerced = coerceStandardProperty(field, control.value);
      if (coerced !== undefined) properties[key] = coerced;
    }
    return properties;
  }

  private findFieldForRow(row: HTMLTableRowElement, key: string): PropertyFieldDef {
    const feature = this.drawingService?.getSelectedApplicationFeature();
    if (!feature) throw new Error("No selected feature.");
    const field = getPropertySchema(feature.geometryType as EditableGeometryType).find((item) => item.key === key);
    if (!field) throw new Error(`Unknown standard property ${key}.`);
    return field;
  }

  private resolvePropertyValue(field: PropertyFieldDef, properties: Record<string, JsonValue>): JsonValue | undefined {
    if (Object.hasOwn(properties, field.key)) return properties[field.key];
    for (const alias of field.aliases ?? []) {
      if (Object.hasOwn(properties, alias)) return properties[alias];
    }
    return field.defaultValue;
  }

  private standardValueToText(field: PropertyFieldDef, value: JsonValue | undefined): string {
    if (field.control === "color") return this.normalizeColor(typeof value === "string" ? value : String(field.defaultValue ?? "#555555"));
    if (value === undefined || value === null) return "";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  }

  private addFeaturePropertyRow(tbody: HTMLTableSectionElement, key = "", value: JsonValue = ""): void {
    const row = document.createElement("tr");
    row.dataset.featurePropertyRow = "true";

    const deleteCell = row.insertCell();
    const keyCell = row.insertCell();
    const valueCell = row.insertCell();

    const deleteButton = document.createElement("button");
    deleteButton.type = "button";
    deleteButton.className = "small-button danger";
    deleteButton.textContent = "-";
    deleteButton.title = "Remove property";
    deleteButton.addEventListener("click", () => row.remove());
    deleteCell.append(deleteButton);

    const keyInput = document.createElement("input");
    keyInput.dataset.featurePropertyKey = "true";
    keyInput.type = "text";
    keyInput.placeholder = "source, period, region_code, notes";
    keyInput.value = key;
    keyCell.append(keyInput);

    const valueInput = document.createElement("input");
    valueInput.dataset.featurePropertyValue = "true";
    valueInput.type = "text";
    valueInput.placeholder = "Value";
    valueInput.value = this.propertyValueToText(value);
    valueCell.append(valueInput);

    tbody.append(row);
  }

  private readFeaturePropertyRows(tbody: HTMLTableSectionElement, reservedKeys = new Set<string>()): Record<string, JsonValue> {
    const properties: Record<string, JsonValue> = {};
    const seenKeys = new Set<string>();
    for (const row of Array.from(tbody.querySelectorAll<HTMLTableRowElement>("[data-feature-property-row]"))) {
      const key = requireElement<HTMLInputElement>("[data-feature-property-key]", row).value.trim();
      if (!key) continue;
      this.validateCustomPropertyKey(key, reservedKeys, seenKeys);
      const rawValue = requireElement<HTMLInputElement>("[data-feature-property-value]", row).value;
      properties[key] = this.parsePropertyValue(rawValue);
      seenKeys.add(key.toLowerCase());
    }
    return properties;
  }

  private validateCustomPropertyKey(key: string, reservedKeys: Set<string>, seenKeys: Set<string>): void {
    if (!PROPERTY_KEY_PATTERN.test(key)) {
      throw new Error(`Custom property "${key}" is not valid. Use letters, numbers, underscore, hyphen, or colon, and start with a letter or underscore.`);
    }
    if (key.toLowerCase().startsWith("__poc")) {
      throw new Error(`Custom property "${key}" is reserved for application metadata.`);
    }
    if (RESERVED_CUSTOM_PROPERTY_KEYS.has(key) || RESERVED_CUSTOM_PROPERTY_KEYS.has(key.toLowerCase())) {
      throw new Error(`Custom property "${key}" is reserved for drawing/editing. Please choose a descriptive metadata name such as source, period, region_code, or notes.`);
    }
    if (Array.from(reservedKeys).some((reserved) => reserved.toLowerCase() === key.toLowerCase())) {
      throw new Error(`${key} is a standard property. Edit it in the Standard properties section.`);
    }
    if (seenKeys.has(key.toLowerCase())) {
      throw new Error(`Custom property "${key}" is duplicated. Keep only one row for each property name.`);
    }
  }

  private propertyValueToText(value: JsonValue): string {
    if (value === null) return "null";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  }

  private parsePropertyValue(value: string): JsonValue {
    const trimmed = value.trim();
    if (trimmed === "") return "";
    if (trimmed === "true") return true;
    if (trimmed === "false") return false;
    if (trimmed === "null") return null;
    if (/^-?\d+(\.\d+)?$/.test(trimmed) && Number.isFinite(Number(trimmed))) return Number(trimmed);
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
      const parsed = JSON.parse(trimmed) as unknown;
      if (this.isJsonValue(parsed)) return parsed;
      throw new Error(`Property value for ${trimmed.slice(0, 32)} is not JSON-compatible.`);
    }
    return value;
  }

  private isJsonValue(value: unknown): value is JsonValue {
    if (value === null) return true;
    if (["string", "number", "boolean"].includes(typeof value)) return true;
    if (Array.isArray(value)) return value.every((item) => this.isJsonValue(item));
    if (typeof value === "object") return Object.values(value as Record<string, unknown>).every((item) => this.isJsonValue(item));
    return false;
  }

  private normalizeColor(value: string): string {
    return /^#[0-9a-f]{6}$/i.test(value) ? value : "#555555";
  }

  private getMultiGeometryWarning(document: GeoJsonDocument): string {
    const counts = { MultiPolygon: 0, MultiLineString: 0, MultiPoint: 0 };
    this.walkGeometryDocument(document, (geometry) => {
      if (geometry.type in counts) counts[geometry.type as keyof typeof counts] += 1;
    });
    const total = counts.MultiPolygon + counts.MultiLineString + counts.MultiPoint;
    if (!total) return "";
    return ` Note: ${total} multi-geometry feature(s) were split into simple editable features; export will keep them as separate features unless you manually regroup them.`;
  }

  private walkGeometryDocument(document: GeoJsonDocument, visit: (geometry: GeoJsonGeometry) => void): void {
    if (document.type === "FeatureCollection") document.features.forEach((feature) => feature.geometry && this.walkGeometryDocument(feature.geometry, visit));
    else if (document.type === "Feature") {
      if (document.geometry) this.walkGeometryDocument(document.geometry, visit);
    } else if (document.type === "GeometryCollection") document.geometries.forEach((geometry) => this.walkGeometryDocument(geometry, visit));
    else visit(document);
  }

  private escapeAttribute(value: string): string {
    return value.replace(/[&<>"]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[char] ?? char);
  }

  private escapeHtml(value: string): string {
    return this.escapeAttribute(value);
  }
}
